/**
 * MegaMotocross school kit (from the school's photos): black jersey whose lower half fades through an orange to red
 * halftone, "MEGAMOTOCROSS.PL" over a big white number and "MOTOCROSS ENDURO CAMP" under it on the back, the helmet
 * emblem on the chest and the back yoke; red pants with black panels; black boots. (Kit colours and the five helmet
 * colourways are garage data: src/ui/logic/livery.ts.)
 *
 * jerseyArt() draws the torso artwork RiderView.setJerseyArt maps onto the body: the left half of the canvas is the
 * front, the right half the back (each seen from outside, so the text reads), v = 0 at JERSEY_ART.yLo below the hip
 * joint, v = 1 at JERSEY_ART.yHi above the shoulder joint. The race number itself is RiderView's own overlay.
 */

/** Vertical extent of the artwork relative to the rider's hip and shoulder joints (m). */
export const JERSEY_ART = { below: 0.14, above: 0.12, width: 2048, height: 1024 } as const;

export const SCHOOL = { black: '#111114', orange: '#ff6a00', red: '#d8202a', white: '#f5f5f5' } as const;

/** v (0 bottom .. 1 top of the art) of a body height y, given hip and shoulder joint heights. */
export function artV(y: number, hip: number, shoulder: number): number {
  const lo = hip - JERSEY_ART.below, hi = shoulder + JERSEY_ART.above;
  return (y - lo) / (hi - lo);
}

/** The helmet emblem: a round badge with a stylised helmet silhouette (brand-neutral stand-in for the logo). */
function emblem(g: CanvasRenderingContext2D, cx: number, cy: number, r: number, logo: HTMLImageElement | null): void {
  if (logo) { g.drawImage(logo, cx - r, cy - r, r * 2, r * 2); return; }
  g.save();
  g.fillStyle = SCHOOL.orange;
  g.beginPath(); g.arc(cx, cy, r, 0, Math.PI * 2); g.fill();
  g.fillStyle = SCHOOL.black;
  g.beginPath();
  g.moveTo(cx - r * 0.55, cy + r * 0.35);
  g.quadraticCurveTo(cx - r * 0.6, cy - r * 0.55, cx + r * 0.15, cy - r * 0.55);
  g.quadraticCurveTo(cx + r * 0.62, cy - r * 0.5, cx + r * 0.62, cy);
  g.lineTo(cx + r * 0.2, cy + r * 0.05);
  g.lineTo(cx + r * 0.35, cy + r * 0.35);
  g.closePath(); g.fill();
  g.restore();
}

/**
 * Draws the jersey artwork. `hip`/`shoulder`: the rider's joint heights (m) to place the back print around the
 * number band RiderView uses (shoulder - 0.33 .. shoulder - 0.07). `logo`: optional brand image.
 */
export function jerseyArt(hip: number, shoulder: number, logo: HTMLImageElement | null = null): HTMLCanvasElement {
  const W = JERSEY_ART.width, H = JERSEY_ART.height;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d')!;
  const yPx = (y: number) => (1 - artV(y, hip, shoulder)) * H; // body height -> canvas row
  g.fillStyle = SCHOOL.black; g.fillRect(0, 0, W, H);
  // Lower half: solid red at the hem, orange above it, then a halftone of shrinking dots up to mid torso.
  const mid = yPx(hip + (shoulder - hip) * 0.62), hem = H;
  const grad = g.createLinearGradient(0, hem, 0, mid);
  grad.addColorStop(0, SCHOOL.red); grad.addColorStop(0.45, SCHOOL.orange); grad.addColorStop(1, SCHOOL.orange);
  const solidTop = hem - (hem - mid) * 0.45;
  g.fillStyle = grad; g.fillRect(0, solidTop, W, hem - solidTop);
  const step = 28;
  for (let y = solidTop; y > mid - step * 2; y -= step) {
    const t = (solidTop - y) / (solidTop - mid + step * 2); // 0 at the solid band, 1 at the top of the fade
    const r = (step / 2) * (1 - t) * 1.05;
    if (r < 0.6) continue;
    g.fillStyle = t < 0.3 ? SCHOOL.red : SCHOOL.orange;
    const odd = Math.round((solidTop - y) / step) % 2;
    for (let x = odd * step / 2; x < W; x += step) { g.beginPath(); g.arc(x, y, r, 0, Math.PI * 2); g.fill(); }
  }
  // Front (left half): emblem on the rider's left chest (viewer's right), small site name under the collar.
  const front = (u: number) => u * (W / 2), back = (u: number) => W / 2 + u * (W / 2);
  emblem(g, front(0.66), yPx(shoulder - 0.1), 52, logo);
  g.fillStyle = SCHOOL.white; g.textAlign = 'center'; g.textBaseline = 'middle';
  g.font = '900 44px "Arial Black", Impact, sans-serif';
  g.fillText('MEGAMOTOCROSS.PL', front(0.5), yPx(shoulder + 0.02), W * 0.3);
  // Back (right half): emblem on the yoke, site name over the number band, camp line under it.
  emblem(g, back(0.5), yPx(shoulder + 0.05), 44, logo);
  g.font = '900 68px "Arial Black", Impact, sans-serif';
  g.fillText('MEGAMOTOCROSS.PL', back(0.5), yPx(shoulder - 0.045), W * 0.24); // within the flat of the back
  g.font = '800 44px "Arial Black", Impact, sans-serif';
  g.fillText('MOTOCROSS ENDURO CAMP', back(0.5), yPx(shoulder - 0.305), W * 0.19);
  return c;
}
